import React, { useState, useRef, useMemo } from "react"
import { motion } from "framer-motion"
import { User, Plane, Globe, Building2 } from "lucide-react"
import { MOTION_VARIANTS, TAB_CAPABILITIES } from "./constants"
import { formatTelegramDate } from "../../utils/date/format"
import { extractMainMessage } from "../../utils/serializers/chat"
import PresenceAvatars from "./PresenceAvatars"
import { resolveDepartmentColorHex } from "../../utils/variables/departaments"
import { getDepartmentColorScheme } from "../utils/color/department_colors"
import { daysColor } from "../utils/color/common"
import { chatItemId } from "../utils/parsers/messages"
import ChatPresenceIndicator from "./ChatPresenceIndicator"

const getDepartment = (departments, id) => {
  if (!departments || !id) return { dept: null, index: -1 }
  const index = departments.findIndex((d) => d.id === id)
  return { dept: index !== -1 ? departments[index] : null, index }
}

const BundleChatItem = ({
  chat,
  index,
  selectedChat,
  onSelectChat,
  t,
  onShowTooltip,
  onHideTooltip,
  departments,
  activeTab,
  presenceUsers,
  isAdmin,
}) => {
  const msgRef = useRef(null)
  const { dept: deptObj, index: deptIndex } = getDepartment(departments, chat.departament_id)
  const departmentName = deptObj?.name ?? null
  const showDeptBadge = TAB_CAPABILITIES[activeTab]?.canFilterByDepartment

  const deptColors = useMemo(
    () => getDepartmentColorScheme(resolveDepartmentColorHex(deptObj, deptIndex)),
    [deptObj?.color, deptIndex]
  )

  const handleMouseEnter = () => {
    if (!chat.message || !msgRef.current) return
    const rect = msgRef.current.getBoundingClientRect()
    onShowTooltip({ message: chat.message, top: rect.top + rect.height / 2, left: rect.right + 8 })
  }

  return (
    <motion.div
      custom={index}
      variants={MOTION_VARIANTS.item}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.2 }}
      onClick={() => onSelectChat(chat)}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={onHideTooltip}
      className={`flex items-start gap-3 px-3 py-3 cursor-pointer transition-colors hover:bg-slate-50 border-b border-slate-100 last:border-0 ${
        selectedChat && chatItemId(selectedChat) === chatItemId(chat)
          ? "bg-blue-50 hover:bg-blue-50"
          : ""
      }`}
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2 mb-1.5">
          <div className="flex flex-wrap gap-1 flex-1 min-w-0">
            <button className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-semibold bg-blue-50 text-blue-700 hover:bg-blue-100 transition-colors">
              #{chat.bundle_id}
            </button>
            {chat.user_name && (
              <button className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs bg-slate-100 text-slate-600 hover:bg-slate-200 transition-colors">
                <User className="w-3 h-3 flex-shrink-0" />
                <span className="max-w-[90px] truncate">{chat.user_name}</span>
              </button>
            )}
            {chat.days_to_flight != null && (
              <span
                className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium ${daysColor(chat.days_to_flight)}`}
              >
                <Plane className="w-3 h-3 flex-shrink-0" />
                {chat.days_to_flight}д
              </span>
            )}
            {chat.state_name && (
              <button className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs bg-indigo-50 text-indigo-600 hover:bg-indigo-100 transition-colors">
                <Globe className="w-3 h-3 flex-shrink-0" />
                <span className="max-w-[80px] truncate">{chat.state_name}</span>
              </button>
            )}
            {showDeptBadge && departmentName && (
              <button
                className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs transition-colors"
                style={{ background: deptColors.bg, color: deptColors.text }}
              >
                <Building2 className="w-3 h-3 flex-shrink-0" />
                <span className="max-w-[80px] truncate">{departmentName}</span>
              </button>
            )}
          </div>
          <div className="flex items-center gap-1.5 flex-shrink-0">
            <ChatPresenceIndicator isAdmin={isAdmin} presenceUsers={presenceUsers} />
            <span className="text-xs text-slate-400 mt-0.5">
              {formatTelegramDate(chat.created_at)}
            </span>
          </div>
        </div>
        <div className="flex items-center justify-between">
          <p
            ref={msgRef}
            className="text-xs text-slate-400 line-clamp-1 flex-1"
            dangerouslySetInnerHTML={{ __html: extractMainMessage(chat.message) }}
          />
          {chat.unread_count > 0 && (
            <div className="ml-2 min-w-[20px] h-5 px-1.5 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0">
              <span className="text-white text-xs font-medium">{chat.unread_count}</span>
            </div>
          )}
        </div>
      </div>
    </motion.div>
  )
}

export default BundleChatItem
